/**
 * glTF, as far as it can be understood without building anything.
 *
 * The format comes two ways round: `.gltf` is a JSON document, and `.glb` is that same
 * document packed into a binary container alongside its buffers. Both are read by three's
 * `GLTFLoader`, which this module does not touch — what lives here is the part that can be
 * decided from the bytes alone, which is recognising one and knowing in advance when one
 * cannot be read.
 *
 * The two refusals are worth making early rather than letting the loader fail:
 *
 * - **Compressed geometry.** Draco, meshopt and Basis Universal each need a decoder this
 *   viewer does not carry, and the loader's own failure is an unhandled rejection deep
 *   inside a promise chain rather than anything a person could act on.
 * - **Files that did not come alone.** A `.gltf` routinely points at a `scene.bin` beside
 *   it, and a file dropped into a browser brings no neighbours. The loader would try to
 *   fetch it against a base path that does not exist, and the network error that results
 *   says nothing about what actually went wrong.
 */

/** `glTF` as a little-endian 32-bit word, which opens every binary file. */
const GLB_MAGIC = 0x46546c67;
/** `JSON`, likewise: the type tag on the chunk holding the document. */
const GLB_JSON_CHUNK = 0x4e4f534a;
const GLB_HEADER_BYTES = 12;
const GLB_CHUNK_HEADER_BYTES = 8;

/**
 * Extensions a file can require that leave it unreadable here, and how to say so.
 *
 * A deny list rather than an allow list. Most extensions describe materials, and a file
 * requiring one we do not know still has geometry that renders — refusing it would be
 * worse than ignoring the extension, which is what the loader does anyway. These three
 * are different: each re-encodes the data itself, so without the decoder there is nothing
 * to draw.
 */
const UNREADABLE_EXTENSIONS = new Map<string, string>([
  ['KHR_draco_mesh_compression', 'compressed with Draco'],
  ['EXT_meshopt_compression', 'compressed with meshopt'],
  ['KHR_texture_basisu', 'stored with Basis Universal textures'],
]);

/** Does this open with the binary container's magic number? */
export function looksLikeGlb(buffer: ArrayBuffer): boolean {
  if (buffer.byteLength < GLB_HEADER_BYTES) return false;
  return new DataView(buffer).getUint32(0, true) === GLB_MAGIC;
}

/**
 * Does this text look like a glTF document?
 *
 * Asks for the `asset` object with a version in it, which the specification requires of
 * every glTF and which no other JSON this viewer might be handed would carry. Testing
 * merely for a leading brace would claim every JSON file in the world.
 */
export function looksLikeGltfJson(text: string): boolean {
  const opening = text.trimStart();
  if (!opening.startsWith('{')) return false;
  return /"asset"\s*:\s*\{[^}]*"version"\s*:/.test(opening);
}

/**
 * The JSON document, however it was packaged.
 *
 * Returns undefined for anything that is not one, rather than throwing: being handed the
 * wrong bytes is an ordinary outcome here, and the caller has a message of its own to give.
 */
export function readGltfDocument(
  buffer: ArrayBuffer,
): Record<string, unknown> | undefined {
  const text = looksLikeGlb(buffer) ? readGlbJsonChunk(buffer) : decode(buffer);
  if (text === undefined) return undefined;

  try {
    const parsed: unknown = JSON.parse(text);
    return isRecord(parsed) ? parsed : undefined;
  } catch {
    return undefined;
  }
}

/**
 * Why this file cannot be read, or undefined if nothing stands in the way.
 *
 * Silence is not a promise that it will render — a document can be well-formed and still
 * defeat the loader. It means only that none of the things we can see coming are present.
 */
export function explainGltfRefusal(buffer: ArrayBuffer): string | undefined {
  const document = readGltfDocument(buffer);
  if (!document) {
    return 'This does not contain a glTF document that could be read.';
  }

  const required = document.extensionsRequired;
  if (Array.isArray(required)) {
    for (const name of required) {
      const description =
        typeof name === 'string' ? UNREADABLE_EXTENSIONS.get(name) : undefined;
      if (description !== undefined) {
        return `This model is ${description}, which needs a decoder this viewer does not carry. Export it again without that compression.`;
      }
    }
  }

  const missing = externalReference(document);
  if (missing !== undefined) {
    return `This glTF refers to ${missing}, which did not come with it. Save it as a .glb instead, which packs everything into one file.`;
  }

  return undefined;
}

/**
 * The first file this document expects to find beside it, if any.
 *
 * Both buffers and images are checked. Textures are not drawn here, so an unreachable
 * image costs nothing to look at — but the loader still waits on it before finishing, so
 * a file with one would hang rather than render.
 */
function externalReference(document: Record<string, unknown>): string | undefined {
  for (const key of ['buffers', 'images']) {
    const entries = document[key];
    if (!Array.isArray(entries)) continue;

    for (const entry of entries) {
      if (!isRecord(entry)) continue;
      const uri = entry.uri;
      // An absent uri is a buffer held inside the container, which is the ordinary case
      // for a `.glb` and exactly what we want. A `data:` uri carries its own contents.
      if (typeof uri !== 'string' || uri === '' || uri.startsWith('data:')) continue;
      return uri;
    }
  }

  return undefined;
}

/** The document out of a binary container's first chunk, which must be the JSON one. */
function readGlbJsonChunk(buffer: ArrayBuffer): string | undefined {
  if (buffer.byteLength < GLB_HEADER_BYTES + GLB_CHUNK_HEADER_BYTES) return undefined;

  const view = new DataView(buffer);
  const length = view.getUint32(GLB_HEADER_BYTES, true);
  const type = view.getUint32(GLB_HEADER_BYTES + 4, true);
  if (type !== GLB_JSON_CHUNK) return undefined;

  const start = GLB_HEADER_BYTES + GLB_CHUNK_HEADER_BYTES;
  // A length running past the end means a truncated download. Reading what is there would
  // produce a JSON parse failure a few lines further on and say less about why.
  if (start + length > buffer.byteLength) return undefined;

  return decode(buffer.slice(start, start + length));
}

function decode(buffer: ArrayBuffer): string {
  return new TextDecoder('utf-8', { fatal: false }).decode(buffer);
}

function isRecord(value: unknown): value is Record<string, unknown> {
  return typeof value === 'object' && value !== null && !Array.isArray(value);
}

/**
 * How many millimetres one glTF unit is.
 *
 * The format carries no unit of its own to honour: it has no field for one, and the
 * specification settles the question instead — "the units for all linear distances are
 * meters". So this is a property of the format rather than of any file, and the only
 * choice is whether to believe it.
 *
 * We do. The alternative, treating a unit as a millimetre, was tried on the grounds that
 * print-adjacent exports tend to be authored that way. That is true of STL and OBJ, which
 * state no convention and which slicers read as millimetres, and it is not true here: glTF
 * does state one, and the exporters that dominate its use honour it — Blender's multiplies
 * by the scene's unit scale precisely so that a model built at real size arrives in metres.
 * Disbelieving the specification made every correctly authored file a thousand times too
 * small.
 *
 * Files authored at no particular scale stay wrong, of course, but they were wrong before
 * and there is nothing in them to be right about.
 */
export const MILLIMETRES_PER_UNIT = 1000;
